import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { saveAs } from "file-saver";
import { BeatLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import { LuDownload, LuEllipsisVertical } from "react-icons/lu";

import { Table } from "../../components/table/table";
import { fetchProfessionals } from "../../services/professionalService";
import { IProfessional } from "../../types";

const LuDownloadIcon = LuDownload as React.ElementType;
const LuEllipsisVerticalIcon = LuEllipsisVertical as React.ElementType;

interface ProfessionalsProps {
  searchQuery?: string; // Accept searchQuery as a prop
}

const Professionals: React.FC<ProfessionalsProps> = ({ searchQuery }) => {
  const navigate = useNavigate();

  const {
    data: professionals,
    isLoading,
    error,
  } = useQuery<IProfessional[]>({
    queryKey: ["professionals", searchQuery],
    queryFn: () => fetchProfessionals(searchQuery),
  });

  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);

  const toggleDropdown = (id: string) => {
    setDropdownOpen(dropdownOpen === id ? null : id);
  };

  const handleView = (id: string) => {
    setDropdownOpen(null);
    navigate(`/users/${id}`, { state: { referrer: "/users/professionals" } });
  };

  const downloadCSV = () => {
    if (!professionals || professionals.length === 0) return;

    const headers = ["User", "Role", "Profession", "Qualification", "Status"];

    const rows = professionals.map((prof) => [
      `"${prof.user.lastName} ${prof.user.firstName}"`,
      `"${prof.user.role}"`,
      `"${prof.user.profession}"`,
      `"${prof.qualification}"`,
      `"${prof.user.userStatus}"`,
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "professionals.csv");
  };

  const columns = [
    {
      header: "User",
      accessorKey: "user",
      cell: ({ row }: { row: { original: IProfessional } }) => (
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 rounded-full">
            <img
              src={row.original.user.image}
              alt="Profile"
              className="w-8 h-8 object-cover rounded-full"
            />
          </div>
          <div>
            <p className="font-medium text-gray-800">
              {row.original.user.lastName} {row.original.user.firstName}
            </p>
            <p className="text-sm text-gray-500">{row.original.user.email}</p>
          </div>
        </div>
      ),
    },
    {
      header: "Role",
      accessorKey: "role",
      cell: ({ row }: { row: { original: IProfessional } }) => (
        <p className="capitalize text-sm text-gray-700">
          {row.original.user.role}
        </p>
      ),
    },
    {
      header: "Profession",
      accessorKey: "profession",
      cell: ({ row }: { row: { original: IProfessional } }) => (
        <p className="capitalize text-sm text-gray-700">
          {row.original.user.profession}
        </p>
      ),
    },
    {
      header: "Qualification",
      accessorKey: "qualification",
      cell: ({ row }: { row: { original: IProfessional } }) => (
        <p className="capitalize text-sm text-gray-700">
          {row.original.qualification}
        </p>
      ),
    },
    {
      header: "Status",
      accessorKey: "user.status",
      cell: ({ row }: { row: { original: IProfessional } }) => {
        return (
          <span
            className={`px-2 py-1 text-sm capitalize ${
              row.original.user.userStatus === "activated"
                ? "text-emerald-500"
                : row.original.user.userStatus === "suspended"
                ? "text-rose-500"
                : "text-amber-500"
            }`}
          >
            {row.original.user.userStatus}
          </span>
        );
      },
    },
    {
      header: "Actions",
      accessorKey: "actions",
      cell: ({ row }: { row: { original: IProfessional } }) => (
        <div className="relative">
          <button
            onClick={() => toggleDropdown(row.original.user._id)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <LuEllipsisVerticalIcon className="text-gray-600 text-lg" />
          </button>
          {dropdownOpen === row.original.user._id && (
            <div
              className="absolute right-0 mt-2 w-24 bg-white shadow-md border rounded-md z-10"
              onClick={() => handleView(row.original.user._id)}
            >
              <button className="w-full text-left px-4 py-2 hover:bg-gray-100 text-sm">
                View
              </button>
            </div>
          )}
        </div>
      ),
    },
  ];

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!(event.target as HTMLElement).closest(".relative")) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  if (isLoading)
    return (
      <div className="p-4">
        <BeatLoader color="#3B82F6" />
      </div>
    );
  if (error)
    return (
      <p className="text-rose-500 text-sm">Error loading professionals.</p>
    );

  return (
    <div className="">
      <div className="px-2 py-4 md:px-4 md:py-2 flex items-center justify-between">
        <h2 className="text-xl">Professionals</h2>
        <div className="flex items-center gap-2">
          <div
            onClick={downloadCSV}
            className="px-4 py-2 bg-transparent border flex items-center justify-between space-x-2 text-gray-800 text-sm rounded-full hover:opacity-50 cursor-pointer transition"
          >
            <LuDownloadIcon />
            <span>CSV</span>
          </div>
        </div>
      </div>
      <Table columns={columns} data={professionals || []} />
    </div>
  );
};

export default Professionals;
